// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getAllReputations, getReputationLevelFromScore } from '../utils/game/ReputationManager';
import { getAlienSpecies } from '../config/AlienSpeciesConfig';
import EventBus, { EVENT_TYPES } from '../utils/system/EventBus';
import './ReputationModal.css';

const ReputationModal = ({ isOpen, onClose }) => {
  const [reputations, setReputations] = useState({});
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    if (isOpen) {
      loadReputations();
    }

    const handleReputationChange = () => {
      loadReputations();
    };

    EventBus.on(EVENT_TYPES.REPUTATION_CHANGED, handleReputationChange);
    return () => EventBus.off('REPUTATION_CHANGED', handleReputationChange);
  }, [isOpen]);

  const loadReputations = () => {
    const allReps = getAllReputations();
    setReputations(allReps);
  };

  const getReputationEntries = () => {
    return Object.entries(reputations)
      .map(([speciesId, score]) => {
        const species = getAlienSpecies(speciesId);
        const level = getReputationLevelFromScore(score);
        return { speciesId, species, score, level };
      })
      .filter(entry => entry.species); // Only show species that exist
  };

  const filteredEntries = getReputationEntries().filter(entry => {
    if (filter === 'all') return true;
    if (filter === 'allied') return entry.score >= 50;
    if (filter === 'friendly') return entry.score >= 10 && entry.score < 50;
    if (filter === 'neutral') return entry.score > -10 && entry.score < 10;
    if (filter === 'unfriendly') return entry.score <= -10 && entry.score > -50;
    if (filter === 'enemy') return entry.score <= -50;
    return true;
  });

  const sortedEntries = [...filteredEntries].sort((a, b) => {
    if (sortBy === 'name') return a.species.name.localeCompare(b.species.name);
    if (sortBy === 'reputation') return b.score - a.score;
    if (sortBy === 'origin') return a.species.origin.localeCompare(b.species.origin);
    return 0;
  });

  const stats = {
    total: getReputationEntries().length,
    allied: getReputationEntries().filter(e => e.score >= 50).length,
    friendly: getReputationEntries().filter(e => e.score >= 10 && e.score < 50).length,
    neutral: getReputationEntries().filter(e => e.score > -10 && e.score < 10).length,
    unfriendly: getReputationEntries().filter(e => e.score <= -10 && e.score > -50).length,
    enemy: getReputationEntries().filter(e => e.score <= -50).length
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="reputation-modal-overlay" onClick={onClose}>
      <div className="reputation-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="reputation-modal-header">
          <div>
            <h2>🤝 REPUTATION</h2>
            <p className="reputation-stats">
              {stats.total} Species Encountered
            </p>
          </div>
          <button className="reputation-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="reputation-summary">
          <div className="reputation-stat allied">
            <span className="stat-label">Allied</span>
            <span className="stat-value">{stats.allied}</span>
          </div>
          <div className="reputation-stat friendly">
            <span className="stat-label">Friendly</span>
            <span className="stat-value">{stats.friendly}</span>
          </div>
          <div className="reputation-stat neutral">
            <span className="stat-label">Neutral</span>
            <span className="stat-value">{stats.neutral}</span>
          </div>
          <div className="reputation-stat unfriendly">
            <span className="stat-label">Unfriendly</span>
            <span className="stat-value">{stats.unfriendly}</span>
          </div>
          <div className="reputation-stat enemy">
            <span className="stat-label">Enemy</span>
            <span className="stat-value">{stats.enemy}</span>
          </div>
        </div>

        <div className="reputation-controls">
          <div className="reputation-filters">
            <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>All</button>
            <button className={filter === 'allied' ? 'active' : ''} onClick={() => setFilter('allied')}>Allied</button>
            <button className={filter === 'friendly' ? 'active' : ''} onClick={() => setFilter('friendly')}>Friendly</button>
            <button className={filter === 'neutral' ? 'active' : ''} onClick={() => setFilter('neutral')}>Neutral</button>
            <button className={filter === 'unfriendly' ? 'active' : ''} onClick={() => setFilter('unfriendly')}>Unfriendly</button>
            <button className={filter === 'enemy' ? 'active' : ''} onClick={() => setFilter('enemy')}>Enemy</button>
          </div>
          <div className="reputation-sort">
            <label>Sort by:</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="name">Name</option>
              <option value="reputation">Reputation</option>
              <option value="origin">Origin</option>
            </select>
          </div>
        </div>

        <div className="reputation-list">
          {sortedEntries.map(entry => (
            <div key={entry.speciesId} className="reputation-entry">
              <div className="reputation-entry-header">
                <div className="reputation-entry-info">
                  <h3>{entry.species.name}</h3>
                  <p className="reputation-origin">{entry.species.origin}</p>
                </div>
                <div className="reputation-entry-level" style={{ color: entry.level.color }}>
                  {entry.level.label}
                </div>
              </div>
              <div className="reputation-bar-container">
                <div className="reputation-bar">
                  <div 
                    className="reputation-bar-fill" 
                    style={{ 
                      width: `${((entry.score + 100) / 200) * 100}%`,
                      background: entry.level.color
                    }}
                  />
                  <div className="reputation-bar-center" />
                </div>
                <div className="reputation-score">{entry.score}</div>
              </div>
              <div className="reputation-effects">
                {entry.score >= 50 && <span className="effect-badge allied">✓ Best Prices</span>}
                {entry.score >= 50 && <span className="effect-badge allied">✓ Aid in Combat</span>}
                {entry.score >= 10 && entry.score < 50 && <span className="effect-badge friendly">✓ Better Prices</span>}
                {entry.score <= -50 && <span className="effect-badge enemy">⚠ Attack on Sight</span>}
                {entry.score <= -50 && <span className="effect-badge enemy">⚠ No Trade</span>}
                {entry.score > -50 && entry.score <= -10 && <span className="effect-badge unfriendly">⚠ Poor Prices</span>}
              </div>
            </div>
          ))}
        </div>

        {sortedEntries.length === 0 && (
          <div className="reputation-empty">
            <p>No species reputations found.</p>
            <p className="reputation-hint">Encounter alien species to build reputation.</p>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ReputationModal;
